// tabs/PolicyTab.jsx
import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  getBranchesThunk,
  getInheritanceSourcesThunk,
  getDependentBranchesThunk,
  validateInheritanceThunk,
  clearInheritanceValidation,
} from "../../../../../store/thunks/branchThunks";
import {
  RATE_POLICIES,
  RATE_MODES,
} from "../../../../../constants/branch.constants";
import { POLICY_DESCRIPTIONS, RATE_MODE_DESCRIPTIONS } from "../constants";
import { toast } from "react-toastify";

const PolicyTab = ({ rateConfig, setRateConfig }) => {
  const dispatch = useDispatch();
  const {
    selectedBranch,
    branches,
    inheritanceSources,
    dependentBranches,
    inheritanceValidation,
    loadingInheritance,
  } = useSelector((state) => state.branch);

  // Load all branches if not loaded
  useEffect(() => {
    if (!branches || branches.length === 0) {
      dispatch(getBranchesThunk({ isActive: true }));
    }
  }, [dispatch, branches]);

  // Load inheritance sources when policy changes or component mounts with inherit policy
  useEffect(() => {
    if (
      (rateConfig.ratePolicy === "INHERIT" ||
        rateConfig.ratePolicy === "HYBRID") &&
      selectedBranch?._id
    ) {
      dispatch(getInheritanceSourcesThunk(selectedBranch._id));
      dispatch(getDependentBranchesThunk(selectedBranch._id));
    }
    return () => {
      dispatch(clearInheritanceValidation());
    };
  }, [rateConfig.ratePolicy, selectedBranch?._id, dispatch]);

  // Handle inheritance source change with validation
  const handleInheritanceChange = async (value) => {
    if (!value) {
      setRateConfig((prev) => ({
        ...prev,
        inheritFromBranch: null,
      }));
      dispatch(clearInheritanceValidation());
      return;
    }

    try {
      const result = await dispatch(
        validateInheritanceThunk({
          branchId: selectedBranch._id,
          targetBranchId: value,
        })
      ).unwrap();

      if (result.valid) {
        setRateConfig((prev) => ({
          ...prev,
          inheritFromBranch: value,
        }));
        toast.success("Valid inheritance source selected");
      } else {
        toast.error(result.message || "Invalid inheritance source");
      }
    } catch (error) {
      toast.error(error || "Failed to validate inheritance");
    }
  };

  // Find the currently inherited branch
  const getCurrentlyInheritedBranch = () => {
    // Get the inheritance ID - prioritize UI state over DB state for immediate feedback
    const inheritId =
      rateConfig.inheritFromBranch ||
      selectedBranch?.rateConfig?.inheritFromBranch;

    if (!inheritId) return null;

    // First check in inheritanceSources
    const fromSources = inheritanceSources.find((s) => s._id === inheritId);
    if (fromSources) return fromSources;

    // Fallback to all branches (for already configured inheritance)
    const fromAllBranches = branches?.find((b) => b._id === inheritId);
    return fromAllBranches;
  };

  const currentlyInheritedBranch = getCurrentlyInheritedBranch();

  // Check if the SAVED branch configuration is actually inheriting
  const isBranchCurrentlyInheriting = () => {
    return (
      (selectedBranch?.rateConfig?.ratePolicy === "INHERIT" ||
        selectedBranch?.rateConfig?.ratePolicy === "HYBRID") &&
      selectedBranch?.rateConfig?.inheritFromBranch
    );
  };

  // Check if the UI state shows inheriting
  const isUIShowingInherit = () => {
    return (
      rateConfig.ratePolicy === "INHERIT" || rateConfig.ratePolicy === "HYBRID"
    );
  };

  // Render inheritance configuration section
  const renderInheritanceSection = () => {
    // CRITICAL: Only show "Currently inheriting from" if the SAVED policy is INHERIT/HYBRID
    // Don't show it just because UI is switched to INHERIT/HYBRID
    const isActuallyInheritingInDB = isBranchCurrentlyInheriting();
    const shouldShowInheritance = isUIShowingInherit();

    // For showing current inheritance, only use the saved inheritFromBranch if actually inheriting in DB
    const currentInheritId = isActuallyInheritingInDB
      ? selectedBranch.rateConfig.inheritFromBranch
      : null;

    // Show current inheritance ONLY if saved policy is INHERIT/HYBRID
    if (
      isActuallyInheritingInDB &&
      currentInheritId &&
      currentlyInheritedBranch
    ) {
      const isCurrentSourceValid = inheritanceSources.some(
        (s) => s._id === currentInheritId
      );

      if (!isCurrentSourceValid && inheritanceSources.length === 0) {
        // Currently inheriting but no other options available
        return (
          <>
            <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <p className="text-sm font-medium text-green-800 mb-1">
                Currently inheriting from:
              </p>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold">
                    {currentlyInheritedBranch.name}
                  </p>
                  <p className="text-xs text-gray-600">
                    {currentlyInheritedBranch.code}
                  </p>
                </div>
                {currentlyInheritedBranch.rateConfig?.ratePolicy && (
                  <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">
                    {currentlyInheritedBranch.rateConfig.ratePolicy} Policy
                  </span>
                )}
              </div>
            </div>

            <div className="p-3 bg-amber-50 rounded-lg">
              <p className="text-sm text-amber-800">
                This branch is currently inheriting from{" "}
                <strong>{currentlyInheritedBranch.name}</strong>. No other
                branches with custom rates are available to change the
                inheritance.
              </p>
            </div>
          </>
        );
      }
    }

    // Normal inheritance selection when sources are available
    if (inheritanceSources.length > 0) {
      // For dropdown value:
      // - Use UI state if available
      // - Otherwise use saved state ONLY if saved policy is INHERIT/HYBRID
      const displayInheritId =
        rateConfig.inheritFromBranch ||
        (isActuallyInheritingInDB
          ? selectedBranch.rateConfig.inheritFromBranch
          : "");

      return (
        <>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Source Branch
              <span className="text-xs text-gray-500 ml-2">
                (Only branches with CUSTOM policy)
              </span>
            </label>
            <div className="relative">
              <select
                value={displayInheritId}
                onChange={(e) => handleInheritanceChange(e.target.value)}
                disabled={inheritanceValidation.validating}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 
                  ${
                    inheritanceValidation.validating
                      ? "bg-gray-100"
                      : "bg-white"
                  }
                  ${
                    inheritanceValidation.message &&
                    !inheritanceValidation.isValid
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
              >
                <option value="">None - Use automatic fallback</option>
                {inheritanceSources.map((branch) => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name} - {branch.code}
                    {branch.isMainBranch && " (Main)"}
                  </option>
                ))}
              </select>

              {inheritanceValidation.validating && (
                <div className="absolute right-2 top-2.5">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                </div>
              )}
            </div>

            {inheritanceValidation.message &&
              !inheritanceValidation.isValid && (
                <p className="mt-1 text-sm text-red-600">
                  {inheritanceValidation.message}
                </p>
              )}

            <p className="mt-2 text-xs text-gray-600">
              {rateConfig.ratePolicy === "INHERIT"
                ? "All rates and configurations will be inherited from the selected branch."
                : "Base rates for metals set to 'INHERIT' in hybrid settings will come from this branch."}
            </p>
          </div>

          {/* Show selected source details based on what's selected in dropdown */}
          {displayInheritId && currentlyInheritedBranch && (
            <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center">
                <div className="flex-1">
                  <p className="text-sm font-medium text-green-800">
                    {isActuallyInheritingInDB
                      ? "Currently inheriting from:"
                      : "Will inherit from:"}
                  </p>
                  <p className="text-sm text-green-700">
                    {currentlyInheritedBranch.name} (
                    {currentlyInheritedBranch.code})
                  </p>
                </div>
                <svg
                  className="w-5 h-5 text-green-600"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
            </div>
          )}

          {/* Info box for leftover inheritFromBranch when not inheriting */}
          {!isUIShowingInherit() &&
            (rateConfig.inheritFromBranch ||
              selectedBranch?.rateConfig?.inheritFromBranch) && (
              <div className="mb-4 p-2 bg-gray-100 rounded text-xs text-gray-600">
                Note: Previous inheritance configuration will be cleared when
                saved with {rateConfig.ratePolicy} policy.
              </div>
            )}
        </>
      );
    }

    // No sources available
    return (
      <div className="p-4 bg-amber-50 rounded-lg">
        <p className="text-sm text-amber-800">
          No branches with custom rates are available for inheritance.
          {selectedBranch?.isMainBranch &&
            " As the main branch, you need to configure custom rates."}
        </p>
      </div>
    );
  };

  // Handle policy change
  const handlePolicyChange = (policy) => {
    // When switching to INHERIT/HYBRID, restore the saved inheritance if exists
    if (
      (policy === "INHERIT" || policy === "HYBRID") &&
      !rateConfig.inheritFromBranch &&
      selectedBranch?.rateConfig?.inheritFromBranch
    ) {
      setRateConfig((prev) => ({
        ...prev,
        ratePolicy: policy,
        inheritFromBranch: selectedBranch.rateConfig.inheritFromBranch,
      }));
    } else {
      setRateConfig((prev) => ({
        ...prev,
        ratePolicy: policy,
      }));
    }
  };

  return (
    <div className="space-y-6">
      {/* Rate Policy Selection */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Rate Policy</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(POLICY_DESCRIPTIONS).map(([policy, description]) => (
            <button
              key={policy}
              onClick={() => handlePolicyChange(policy)}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                rateConfig.ratePolicy === policy
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <div className="font-semibold mb-1">{RATE_POLICIES[policy]}</div>
              <div className="text-sm text-gray-600">{description}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Inheritance Configuration - Only show for INHERIT or HYBRID policies */}
      {isUIShowingInherit() && (
        <div className="bg-indigo-50 p-4 rounded-lg">
          <h4 className="font-medium mb-3">
            {rateConfig.ratePolicy === "INHERIT"
              ? "Inheritance Configuration"
              : "Hybrid Inheritance Source"}
          </h4>

          {/* Current Branch Info */}
          <div className="mb-4 p-3 bg-white rounded-lg border border-indigo-200">
            <p className="text-xs text-gray-500 mb-1">Currently configuring:</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-sm">{selectedBranch?.name}</p>
                <p className="text-xs text-gray-600">{selectedBranch?.code}</p>
              </div>
              {selectedBranch?.isMainBranch && (
                <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                  Main Branch
                </span>
              )}
            </div>
            {/* Show current saved policy if different from UI */}
            {selectedBranch?.rateConfig?.ratePolicy &&
              selectedBranch.rateConfig.ratePolicy !==
                rateConfig.ratePolicy && (
                <p className="text-xs text-amber-600 mt-2">
                  Currently saved as:{" "}
                  {RATE_POLICIES[selectedBranch.rateConfig.ratePolicy]}
                </p>
              )}
          </div>

          {/* Render appropriate inheritance section */}
          {loadingInheritance ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm">
                Loading inheritance options...
              </span>
            </div>
          ) : (
            renderInheritanceSection()
          )}

          {/* Dependent Branches Warning */}
          {dependentBranches && dependentBranches.length > 0 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex">
                <svg
                  className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <div className="flex-1">
                  <p className="text-sm font-medium text-yellow-800">
                    Branches depending on this branch:
                  </p>
                  <ul className="mt-1 text-xs text-yellow-700 space-y-0.5">
                    {dependentBranches.map((b) => (
                      <li key={b._id}>
                        • {b.name} ({b.code})
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-yellow-600 mt-2">
                    Changing this branch's policy may affect these dependent
                    branches.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Rate Mode Selection */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Rate Mode</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(RATE_MODE_DESCRIPTIONS).map(([mode, description]) => (
            <button
              key={mode}
              onClick={() =>
                setRateConfig((prev) => ({ ...prev, rateMode: mode }))
              }
              disabled={rateConfig.ratePolicy === "INHERIT"}
              className={`p-4 rounded-lg border-2 text-left transition-all ${
                rateConfig.rateMode === mode
                  ? "border-green-500 bg-green-50"
                  : "border-gray-200 hover:border-gray-300"
              } ${
                rateConfig.ratePolicy === "INHERIT"
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
            >
              <div className="font-semibold mb-1">{RATE_MODES[mode]}</div>
              <div className="text-sm text-gray-600">{description}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PolicyTab;
